import React, { useState, useEffect } from 'react';
import { Users, Search, Mail, Phone, MapPin, ChevronLeft, ChevronRight, TrendingUp, ShoppingBag, UserPlus, IndianRupee, Award, AlertCircle } from 'lucide-react';
import { apiRequest } from '../../../utils/api-request';
import SettingsSlideModal from '../../../components/Modal/SettingsSlideModal';

interface Customer {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  phone: string | null;
  orders_count: number;
  total_spent: string;
  created_at: string;
  updated_at: string;
  state: string;
  default_address?: {
    address1?: string;
    address2?: string;
    city: string;
    province: string;
    country: string;
    zip?: string;
  };
}

interface TopCustomer {
  id: string;
  name: string;
  email: string;
  orders_count: number;
  total_spent: string;
}

interface CustomerStats {
  totalCustomers: number;
  customersWithOrders: number;
  customersWithZeroOrders: number;
  totalRevenue: number;
  averageOrdersPerCustomer: string;
  newCustomersThisMonth: number;
  topCustomersByOrders: TopCustomer[];
  topCustomersBySpent: TopCustomer[];
}

const ITEMS_PER_PAGE = 20;

const CustomersPage: React.FC = () => {
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [stats, setStats] = useState<CustomerStats | null>(null);
  const [isLoadingStats, setIsLoadingStats] = useState(true);
  const [isSearching, setIsSearching] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalCustomers, setTotalCustomers] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [selectedCustomer, setSelectedCustomer] = useState<Customer | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);

  // Load stats on mount
  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    setIsLoadingStats(true);
    try {
      const response = await apiRequest<CustomerStats>('/shopify/customers/stats', { method: 'GET' });
      if (response.success && response.data) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Failed to load customer stats:', error);
    } finally {
      setIsLoadingStats(false);
    }
  };

  const searchCustomers = async (query: string, page: number = 1) => {
    if (!query || query.trim().length < 2) {
      return;
    }

    setIsSearching(true);
    setHasSearched(true);
    try {
      const response = await apiRequest<Customer[]>(
        `/shopify/customers/search?q=${encodeURIComponent(query)}&page=${page}&limit=${ITEMS_PER_PAGE}`,
        { method: 'GET' }
      );
      if (response.success && response.data) {
        setCustomers(response.data);
        const pagination = (response as any).pagination;
        if (pagination) {
          setTotalCustomers(pagination.total);
          setTotalPages(pagination.totalPages);
          setCurrentPage(pagination.page);
        }
      }
    } catch (error) {
      console.error('Failed to search customers:', error);
    } finally {
      setIsSearching(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchInput.trim().length >= 2) {
      setSearchQuery(searchInput);
      searchCustomers(searchInput, 1);
    }
  };

  const handlePageChange = (page: number) => {
    if (searchQuery) {
      searchCustomers(searchQuery, page);
    }
  };

  const handleCustomerClick = (customer: Customer) => {
    setSelectedCustomer(customer);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedCustomer(null);
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
  };

  const formatCurrency = (amount: number | string, compact: boolean = false) => {
    const num = typeof amount === 'string' ? parseFloat(amount) : amount;

    if (compact && num >= 10000000) {
      // Crores (1Cr = 10,000,000)
      return '₹' + (num / 10000000).toFixed(1) + 'Cr';
    } else if (compact && num >= 100000) {
      // Lakhs (1L = 100,000)
      return '₹' + (num / 100000).toFixed(1) + 'L';
    } else if (compact && num >= 1000) {
      // Thousands
      return '₹' + (num / 1000).toFixed(1) + 'K';
    }

    return '₹' + new Intl.NumberFormat('en-IN', {
      maximumFractionDigits: 0
    }).format(num);
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearchQuery('');
    setCustomers([]);
    setHasSearched(false);
    setCurrentPage(1);
    setTotalCustomers(0);
    setTotalPages(0);
  };

  return (
    <div className="w-full">
      {/* Header */}
      <div className="sticky top-0 z-10 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
        <div className="flex items-center justify-between gap-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-brandColor rounded-lg text-white">
              <Users className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-lg font-semibold text-gray-900 dark:text-white">
                Customers
              </h1>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Search and manage your Shopify customers
              </p>
            </div>
          </div>

          {/* Search Form */}
          <form onSubmit={handleSearch} className="flex-1 max-w-xl">
            <div className="relative flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <input
                  type="text"
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  placeholder="Search by name, email, phone, or Shopify ID..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:border-brandColor dark:bg-gray-800 dark:text-white"
                />
              </div>
              <button
                type="submit"
                disabled={searchInput.trim().length < 2}
                className="px-4 py-2 bg-brandColor text-white rounded-lg hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Search
              </button>
              {hasSearched && (
                <button
                  type="button"
                  onClick={clearSearch}
                  className="px-4 py-2 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50"
                >
                  Clear
                </button>
              )}
            </div>
          </form>
        </div>
      </div>

      <div className="p-6 bg-gray-50 dark:bg-gray-900 min-h-screen">
        {/* Stats Section - Always visible */}
        {isLoadingStats ? (
          <div className="flex items-center justify-center h-32">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brandColor"></div>
          </div>
        ) : stats && (
          <div className="mb-6 space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Users className="w-5 h-5 text-blue-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                      {stats.totalCustomers.toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-500">Total Customers</p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg">
                    <ShoppingBag className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                      {stats.customersWithOrders.toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-500">With Orders</p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-orange-100 rounded-lg">
                    <AlertCircle className="w-5 h-5 text-orange-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                      {stats.customersWithZeroOrders.toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-500">Zero Orders</p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-purple-100 rounded-lg">
                    <IndianRupee className="w-5 h-5 text-purple-600" />
                  </div>
                  <div>
                    <p className="text-xl font-bold text-gray-900 dark:text-white">
                      {formatCurrency(stats.totalRevenue, true)}
                    </p>
                    <p className="text-xs text-gray-500">Total Revenue</p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-indigo-100 rounded-lg">
                    <TrendingUp className="w-5 h-5 text-indigo-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                      {stats.averageOrdersPerCustomer}
                    </p>
                    <p className="text-xs text-gray-500">Avg Orders/Customer</p>
                  </div>
                </div>
              </div>

              <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-teal-100 rounded-lg">
                    <UserPlus className="w-5 h-5 text-teal-600" />
                  </div>
                  <div>
                    <p className="text-2xl font-bold text-gray-900 dark:text-white">
                      {stats.newCustomersThisMonth.toLocaleString()}
                    </p>
                    <p className="text-xs text-gray-500">New This Month</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Top Customers */}
            <div className="grid md:grid-cols-2 gap-6">
              {/* Top by Orders */}
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 dark:bg-gray-750 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2">
                    <Award className="w-4 h-4 text-yellow-500" />
                    <h3 className="font-semibold text-gray-900 dark:text-white text-sm">Top Customers by Orders</h3>
                  </div>
                </div>
                <div className="divide-y divide-gray-100 dark:divide-gray-700">
                  {stats.topCustomersByOrders.length === 0 ? (
                    <p className="p-4 text-sm text-gray-500 text-center">No data available</p>
                  ) : (
                    stats.topCustomersByOrders.map((customer, index) => (
                      <div key={customer.id} className="px-4 py-3 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-750">
                        <div className="flex items-center gap-3">
                          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                            index === 0 ? 'bg-yellow-100 text-yellow-700' :
                            index === 1 ? 'bg-gray-100 text-gray-700' :
                            index === 2 ? 'bg-orange-100 text-orange-700' :
                            'bg-gray-50 text-gray-600'
                          }`}>
                            {index + 1}
                          </span>
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">{customer.name}</p>
                            <p className="text-xs text-gray-500">{customer.email}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-brandColor">{customer.orders_count} orders</p>
                          <p className="text-xs text-gray-500">{formatCurrency(customer.total_spent)}</p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>

              {/* Top by Spent */}
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 dark:bg-gray-750 border-b border-gray-200 dark:border-gray-700">
                  <div className="flex items-center gap-2">
                    <IndianRupee className="w-4 h-4 text-green-500" />
                    <h3 className="font-semibold text-gray-900 dark:text-white text-sm">Top Customers by Revenue</h3>
                  </div>
                </div>
                <div className="divide-y divide-gray-100 dark:divide-gray-700">
                  {stats.topCustomersBySpent.length === 0 ? (
                    <p className="p-4 text-sm text-gray-500 text-center">No data available</p>
                  ) : (
                    stats.topCustomersBySpent.map((customer, index) => (
                      <div key={customer.id} className="px-4 py-3 flex items-center justify-between hover:bg-gray-50 dark:hover:bg-gray-750">
                        <div className="flex items-center gap-3">
                          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${
                            index === 0 ? 'bg-green-100 text-green-700' :
                            index === 1 ? 'bg-gray-100 text-gray-700' :
                            index === 2 ? 'bg-emerald-100 text-emerald-700' :
                            'bg-gray-50 text-gray-600'
                          }`}>
                            {index + 1}
                          </span>
                          <div>
                            <p className="text-sm font-medium text-gray-900 dark:text-white">{customer.name}</p>
                            <p className="text-xs text-gray-500">{customer.email}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <p className="text-sm font-bold text-green-600">{formatCurrency(customer.total_spent)}</p>
                          <p className="text-xs text-gray-500">{customer.orders_count} orders</p>
                        </div>
                      </div>
                    ))
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Search Results Section */}
        {hasSearched && (
          <div className="mt-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                Search Results {searchQuery && <span className="text-gray-500 font-normal">for "{searchQuery}"</span>}
              </h2>
              {totalCustomers > 0 && (
                <p className="text-sm text-gray-500">
                  Showing {((currentPage - 1) * ITEMS_PER_PAGE) + 1}-{Math.min(currentPage * ITEMS_PER_PAGE, totalCustomers)} of {totalCustomers.toLocaleString()} customer(s)
                </p>
              )}
            </div>

            {isSearching ? (
              <div className="flex items-center justify-center h-32">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-brandColor"></div>
                <span className="ml-2 text-gray-600">Searching...</span>
              </div>
            ) : customers.length === 0 ? (
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-8 text-center">
                <Users className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No customers found</h3>
                <p className="text-sm text-gray-500">Try adjusting your search query</p>
              </div>
            ) : (
              <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50 dark:bg-gray-750 border-b border-gray-200 dark:border-gray-700">
                      <tr>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Orders</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Spent</th>
                        <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                      {customers.map((customer) => (
                        <tr key={customer.id} className="hover:bg-gray-50 dark:hover:bg-gray-750">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="flex items-center">
                              <div className="h-10 w-10 rounded-full bg-brandColor flex items-center justify-center text-white font-semibold">
                                {(customer.first_name?.[0] || '').toUpperCase()}{(customer.last_name?.[0] || '').toUpperCase()}
                              </div>
                              <div className="ml-3">
                                <button
                                  onClick={() => handleCustomerClick(customer)}
                                  className="text-sm font-medium text-brandColor hover:opacity-80 cursor-pointer text-left"
                                >
                                  {customer.first_name || ''} {customer.last_name || ''}
                                </button>
                                <div className="text-xs text-gray-500">ID: {customer.id}</div>
                              </div>
                            </div>
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">{customer.email}</td>
                          <td className="px-6 py-4 text-sm text-gray-500">{customer.phone || '-'}</td>
                          <td className="px-6 py-4 text-sm text-gray-500">
                            {customer.default_address
                              ? `${customer.default_address.city}, ${customer.default_address.province}`
                              : '-'}
                          </td>
                          <td className="px-6 py-4 text-sm text-gray-500">{customer.orders_count}</td>
                          <td className="px-6 py-4 text-sm font-medium text-gray-900">₹{customer.total_spent}</td>
                          <td className="px-6 py-4">
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              customer.state === 'enabled'
                                ? 'bg-green-100 text-green-800'
                                : 'bg-gray-100 text-gray-800'
                            }`}>
                              {customer.state}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex items-center justify-between">
                    <div className="text-sm text-gray-700 dark:text-gray-300">
                      Showing <span className="font-medium">{((currentPage - 1) * ITEMS_PER_PAGE) + 1}</span> to{' '}
                      <span className="font-medium">{Math.min(currentPage * ITEMS_PER_PAGE, totalCustomers)}</span> of{' '}
                      <span className="font-medium">{totalCustomers.toLocaleString()}</span> results
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        onClick={() => handlePageChange(Math.max(1, currentPage - 1))}
                        disabled={currentPage === 1}
                        className="px-3 py-1 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                      >
                        <ChevronLeft className="h-4 w-4" />
                        <span>Previous</span>
                      </button>
                      <span className="text-sm text-gray-600">
                        Page {currentPage} of {totalPages}
                      </span>
                      <button
                        onClick={() => handlePageChange(Math.min(totalPages, currentPage + 1))}
                        disabled={currentPage === totalPages}
                        className="px-3 py-1 rounded-md border border-gray-300 bg-white text-gray-700 hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
                      >
                        <span>Next</span>
                        <ChevronRight className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Initial State - No search yet */}
        {!hasSearched && !isLoadingStats && (
          <div className="mt-6 bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-12 text-center">
            <Search className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">Search for Customers</h3>
            <p className="text-sm text-gray-500 max-w-md mx-auto">
              With {stats?.totalCustomers.toLocaleString() || '600K+'} customers in your database,
              use the search bar above to find specific customers by name, email, phone, or Shopify Customer ID.
            </p>
          </div>
        )}
      </div>

      {/* Customer Details Slider */}
      <SettingsSlideModal
        isOpen={isModalOpen}
        onClose={closeModal}
        title={selectedCustomer ? `${selectedCustomer.first_name || ''} ${selectedCustomer.last_name || ''}` : ''}
        subtitle={`Shopify Customer ID: ${selectedCustomer?.id}`}
        icon={
          selectedCustomer ? (
            <div className="h-8 w-8 rounded-full bg-brandColor flex items-center justify-center text-white font-semibold text-sm">
              {(selectedCustomer.first_name?.[0] || '').toUpperCase()}{(selectedCustomer.last_name?.[0] || '').toUpperCase()}
            </div>
          ) : (
            <Users className="w-6 h-6" />
          )
        }
        width="800px"
      >
        {selectedCustomer && (
          <div className="flex-1 overflow-y-auto">
            <div className="px-6 py-4 space-y-6">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">Email</label>
                  <div className="flex items-center mt-1">
                    <Mail className="h-4 w-4 text-gray-400 mr-2" />
                    <p className="text-sm text-gray-900">{selectedCustomer.email}</p>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">Phone</label>
                  <div className="flex items-center mt-1">
                    <Phone className="h-4 w-4 text-gray-400 mr-2" />
                    <p className="text-sm text-gray-900">{selectedCustomer.phone || 'N/A'}</p>
                  </div>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">Orders Count</label>
                  <p className="text-sm text-gray-900 mt-1">{selectedCustomer.orders_count}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">Total Spent</label>
                  <p className="text-sm font-semibold text-gray-900 mt-1">₹{selectedCustomer.total_spent}</p>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">Status</label>
                  <span className={`inline-block px-2 py-1 text-xs rounded-full mt-1 ${
                    selectedCustomer.state === 'enabled'
                      ? 'bg-green-100 text-green-800'
                      : 'bg-gray-100 text-gray-800'
                  }`}>
                    {selectedCustomer.state}
                  </span>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500 uppercase">Joined Date</label>
                  <p className="text-sm text-gray-900 mt-1">{formatDate(selectedCustomer.created_at)}</p>
                </div>
              </div>

              {selectedCustomer.default_address && (
                <div>
                  <h4 className="text-sm font-semibold text-gray-900 mb-3 flex items-center">
                    <MapPin className="h-4 w-4 mr-2" />
                    Default Address
                  </h4>
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <p className="text-sm text-gray-900">
                      {selectedCustomer.default_address.address1}
                      {selectedCustomer.default_address.address2 && (
                        <><br />{selectedCustomer.default_address.address2}</>
                      )}
                      <br />
                      {selectedCustomer.default_address.city}, {selectedCustomer.default_address.province}{' '}
                      {selectedCustomer.default_address.zip}
                      <br />
                      {selectedCustomer.default_address.country}
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </SettingsSlideModal>
    </div>
  );
};

export default CustomersPage;
